// Auto word → picture resolver for question answers.
//
// Pre-readers can't read a word-answer like "apple", so any plain-text option
// that has no `iconKey` and isn't an auto-drawn count/color/letter falls back
// to a recognizable PICTURE here. We map the word to a Twemoji codepoint and
// render its PNG (same free jsDelivr CDN the stub library uses) — because it's
// an <img>, it renders identically on web, phones, and old TV Chromium
// (no reliance on a system emoji font).
//
// This is a render-time bridge. The durable path is authoring/generation
// attaching an `iconKey` (or a scene-extracted image) to every option.

const CDN = "https://cdn.jsdelivr.net/gh/jdecked/twemoji@15.1.0/assets/72x72";

// Lowercased word → Twemoji codepoint filename (no extension).
const WORD_TO_CP: Record<string, string> = {
  // ── animals ──────────────────────────────────────────────
  dog: "1f436", puppy: "1f436", cat: "1f431", kitten: "1f431",
  cow: "1f42e", pig: "1f437", frog: "1f438", bird: "1f426",
  fish: "1f41f", bear: "1f43b", lion: "1f981", tiger: "1f42f",
  monkey: "1f435", horse: "1f434", pony: "1f434", duck: "1f986",
  chicken: "1f414", chick: "1f425", rabbit: "1f430", bunny: "1f430",
  mouse: "1f42d", elephant: "1f418", penguin: "1f427", owl: "1f989",
  bee: "1f41d", butterfly: "1f98b", snake: "1f40d", turtle: "1f422",
  whale: "1f433", dolphin: "1f42c", octopus: "1f419", crab: "1f980",
  sheep: "1f411", goat: "1f410", fox: "1f98a", panda: "1f43c",
  koala: "1f428", snail: "1f40c", ladybug: "1f41e", spider: "1f577",
  // ── food ─────────────────────────────────────────────────
  apple: "1f34e", banana: "1f34c", orange: "1f34a", grape: "1f347",
  grapes: "1f347", strawberry: "1f353", watermelon: "1f349",
  lemon: "1f34b", cherry: "1f352", cherries: "1f352", peach: "1f351",
  pear: "1f350", pineapple: "1f34d", carrot: "1f955", corn: "1f33d",
  bread: "1f35e", egg: "1f95a", milk: "1f95b", cheese: "1f9c0",
  pizza: "1f355", cookie: "1f36a", cake: "1f370", candy: "1f36c",
  lollipop: "1f36d", doughnut: "1f369", donut: "1f369",
  "ice cream": "1f366", icecream: "1f366", tomato: "1f345",
  // ── nature / sky ─────────────────────────────────────────
  sun: "2600", moon: "1f319", star: "2b50", cloud: "2601",
  rainbow: "1f308", tree: "1f333", flower: "1f338", leaf: "1f343",
  snowflake: "2744", snow: "2744", fire: "1f525", water: "1f4a7",
  rain: "1f327", mountain: "26f0",
  // ── vehicles ─────────────────────────────────────────────
  car: "1f697", plane: "2708", airplane: "2708", boat: "26f5",
  ship: "1f6a2", train: "1f686", bus: "1f68c", truck: "1f69a",
  bike: "1f6b2", bicycle: "1f6b2", rocket: "1f680", helicopter: "1f681",
  // ── objects ──────────────────────────────────────────────
  ball: "26bd", balloon: "1f388", book: "1f4d6", house: "1f3e0",
  home: "1f3e0", key: "1f511", clock: "1f550", phone: "1f4f1",
  hat: "1f3a9", shoe: "1f45f", shirt: "1f455", umbrella: "2602",
  gift: "1f381", present: "1f381", drum: "1f941", guitar: "1f3b8",
  crown: "1f451", bell: "1f514", heart: "2764", cup: "1f964",
  // ── shapes ───────────────────────────────────────────────
  circle: "1f535", square: "1f7e6", triangle: "1f53a", diamond: "1f48e",
};

/**
 * Best-effort: returns the Twemoji PNG URL for a word-answer, or null when we
 * have no picture for it (the caller then keeps the text as a last resort).
 */
export function optionPictureUrl(label: string | undefined): string | null {
  const base = String(label || "")
    .trim()
    .toLowerCase()
    .replace(/[.!?]+$/, "");
  if (!base) return null;
  const noArticle = base.replace(/^(a|an|the)\s+/, "");
  const candidates = [
    base,
    noArticle,
    noArticle.replace(/s$/, ""), // plural → singular (cats → cat)
    base.replace(/s$/, ""),
  ];
  for (const c of candidates) {
    if (WORD_TO_CP[c]) return `${CDN}/${WORD_TO_CP[c]}.png`;
  }
  return null;
}
